import { jotaiStore } from '@shared/store/jotai/jotai-store';
import { dcbAtom } from '@entities/dcb/store/dcb.atom';

import type { DCBState } from '@entities/dcb/model/types';
import { PAYMENT_METHODS } from '@entities/payment-plan';

const omitUndefined = <T extends Record<string, unknown>>(obj: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined)
  ) as Partial<T>;

const setDcb = (next: (prev: DCBState) => DCBState): void => {
  jotaiStore.set(dcbAtom, next);
};

export type DetectedOperatorInput = {
  operatorName: string;
  operatorValue: string;
  isShownMsisdn?: number;
  operatorLogo?: string;
  fullOperatorDetails: unknown;
};

export type ChangeOperatorInput = {
  operatorName: string;
  operatorValue: string;
  operatorLogo: string;
  isShownMsisdn: number;
  error?: string | null;
  fullOperatorDetails: unknown;
};

export const applyChangeOperatorSelection = (operator: ChangeOperatorInput): void => {
  setDcb((prev) => ({
    ...prev,
    operator: {
      name: operator.operatorName,
      value: operator.operatorValue,
      logo: operator.operatorLogo,
      isShownMsisdn: operator.isShownMsisdn,
      error: operator.error ?? null,
    },
    cacheData: {
      operator: {
        name: operator.operatorName,
        value: operator.operatorValue,
        logo: operator.operatorLogo,
        isShownMsisdn: operator.isShownMsisdn,
      },
    },
    operatorSelectedDetails: operator.fullOperatorDetails,
  }));
};

export const applyDetectedOperator = (details: DetectedOperatorInput): void => {
  setDcb((prev) => ({
    ...prev,
    operator: {
      name: details.operatorName,
      value: details.operatorValue,
      logo: details.operatorLogo ?? prev.operator.logo,
      isShownMsisdn: details.isShownMsisdn ?? prev.operator.isShownMsisdn,
      error: null,
    },
    operatorSelectedDetails: details.fullOperatorDetails,
    operatorDetectionFinished: true,
  }));
};

export const writeSubscribeRequestData = (payload: unknown): void => {
  setDcb((prev) => ({ ...prev, subscribeRequestData: payload }));
};

export const writeSource = (source: string): void => {
  setDcb((prev) => ({ ...prev, source }));
};

export const writePropsInState = (payload: DCBState['propsInState']): void => {
  setDcb((prev) => ({ ...prev, propsInState: payload }));
};

export const writeHomeRequested = (): void => {
  setDcb((prev) => ({ ...prev, viewloading: true }));
};

export const writeHomeReceived = (payload: Record<string, any>): void => {
  setDcb((prev) => ({
    ...prev,
    viewloading: false,
    homeData: payload as DCBState['homeData'],
    dataRetrieved: true,
    dataRetrievedLanguage: payload.dataRetrievedLanguage,
    countryCode: `+${payload.countryCode}`,
    operators: payload.operators,
    dcbHomeReceived: true,
  }));
};

export const writeStoredHomeUrl = (homeUrl: string): void => {
  setDcb((prev) => ({ ...prev, homeUrl }));
};

export const patchDcbState = (partial: Partial<DCBState>): void => {
  setDcb((prev) => ({ ...prev, ...omitUndefined(partial as Record<string, unknown>) }));
};

export const patchHomeDataPartial = (partial: Partial<DCBState['homeData']>): void => {
  setDcb((prev) => ({
    ...prev,
    homeData: {
      ...prev.homeData,
      ...omitUndefined(partial as Record<string, unknown>),
    },
  }));
};

export const writeSubscribeRequested = (): void => {
  setDcb((prev) => ({ ...prev, btnLoading: true }));
};

export const writeSubscribeCompleted = (payload: Record<string, any>): void => {
  setDcb((prev) => ({
    ...prev,
    btnLoading: false,
    subscribeData: payload as DCBState['subscribeData'],
    fallbackOperator: payload?.operatorName ? payload.operatorName : null,
  }));
};

export const writeSubscribeStopped = (): void => {
  setDcb((prev) => ({ ...prev, btnLoading: false }));
};

export const writeFlowErrorStoppingLoading = (errorPayload: unknown): void => {
  setDcb((prev) => ({
    ...prev,
    btnLoading: false,
    error: errorPayload,
    viewloading: false,
  }));
};

export const writeOtpSubmitStarted = (): void => {
  setDcb((prev) => ({ ...prev, btnLoading: true }));
};

export const writeOtpResult = (payload: Record<string, any> | undefined): void => {
  setDcb((prev) => ({
    ...prev,
    btnLoading: false,
    otpData: payload,
    otpError: !!(payload?.otpError),
  }));
};

export const writeReachedThankYouFromOtp = (): void => {
  setDcb((prev) => ({ ...prev, reachedThankYou: true }));
};

export const writeOtpFailure = (errorPayload: unknown): void => {
  setDcb((prev) => ({
    ...prev,
    btnLoading: false,
    error: errorPayload,
    otpError: true,
  }));
};

export const writeResendOtpTick = (): void => {
  setDcb((prev) => ({ ...prev, timerActive: false, timerCount: prev.timerCount + 1 }));
};

export const writeResendOtpSuccess = (payload: unknown): void => {
  setDcb((prev) => ({ ...prev, resendOtpData: payload }));
};

export const writeResendOtpFailure = (errorPayload: unknown): void => {
  setDcb((prev) => ({ ...prev, error: errorPayload }));
};

export const writeThankyouRequested = (): void => {
  setDcb((prev) => ({ ...prev, viewloading: true }));
};

export const writeThankyouSuccess = (payload: unknown): void => {
  setDcb((prev) => ({ ...prev, viewloading: false, thankyouData: payload }));
};

export const writeThankyouFailure = (errorPayload: unknown): void => {
  setDcb((prev) => ({ ...prev, viewloading: false, error: errorPayload }));
};

export const resetTimerActive = (): void => {
  setDcb((prev) => ({ ...prev, timerActive: true }));
};

export const showPromoModal = (): void => {
  setDcb((prev) => ({ ...prev, isPromoModalShown: true }));
};

export const hidePromoModal = (): void => {
  setDcb((prev) => ({ ...prev, isPromoModalShown: false }));
};

export const setPromoStep = (step: number): void => {
  setDcb((prev) => ({
    ...prev,
    promoStep: step,
    isPromoValid: step === 3,
    invalidPromocodeError: !(step === 2 || step === 3),
  }));
};

export const flagInvalidPromocode = (): void => {
  setDcb((prev) => ({ ...prev, invalidPromocodeError: true }));
};

export const setOfferCode = (code: string): void => {
  setDcb((prev) => ({ ...prev, offerCode: code }));
};

export const setUserSelectedLanguageManually = (): void => {
  setDcb((prev) => ({ ...prev, userSelectedLanguageManually: true }));
};

export const setCacheData = (payload: unknown): void => {
  setDcb((prev) => ({ ...prev, cacheData: payload }));
};

export const clearCacheData = (): void => {
  setDcb((prev) => ({ ...prev, cacheData: {} }));
};

export const setDefaultPaymentMethod = (method = PAYMENT_METHODS.Card): void => {
  setDcb((prev) => ({ ...prev, defaultPaymentMethod: method }));
};

export const setInappRedirect = (flag: boolean): void => {
  setDcb((prev) => ({ ...prev, isInapp: flag }));
};
